import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { AlertCircle, Info } from "lucide-react";
import { AmbiguousAssignment, RegionalMatch } from "../../types/excel-upload.types";

interface AmbiguityResolutionContentProps {
  ambiguousItems: AmbiguousAssignment[];
  onConfirm: (resolutions: Array<{ store_id: number; regional_id: number }>) => void;
  onCancel: () => void;
}

interface Resolution {
  store_id: number;
  selected_regional_id: number | null;
}

export const AmbiguityResolutionContent: React.FC<AmbiguityResolutionContentProps> = ({
  ambiguousItems,
  onConfirm,
  onCancel,
}) => {
  const [resolutions, setResolutions] = useState<Resolution[]>([]);

  useEffect(() => {
    if (ambiguousItems.length > 0) {
      setResolutions(
        ambiguousItems.map((item) => ({
          store_id: item.store_id,
          selected_regional_id: null,
        }))
      );
    }
  }, [ambiguousItems]);

  const handleRegionalSelect = (store_id: number, regional_id: string) => {
    setResolutions((prev) =>
      prev.map((res) =>
        res.store_id === store_id
          ? { ...res, selected_regional_id: Number(regional_id) }
          : res
      )
    );
  };

  const allResolved =
  resolutions.length === ambiguousItems.length &&
  resolutions.length > 0 &&
  resolutions.every((res) => res.selected_regional_id !== null);

const handleConfirm = () => {
  if (!allResolved) return;

  const confirmedResolutions = resolutions.map((res) => ({
    store_id: res.store_id,
    regional_id: res.selected_regional_id!,
  }));

  onConfirm(confirmedResolutions);
};

  return (
    <div className="space-y-6 max-h-[60vh] overflow-y-auto px-1">
      {/* Alert de aviso */}
      <Alert variant="default" className="border-orange-500 bg-orange-50">
        <AlertCircle className="h-4 w-4 text-orange-600" />
        <AlertDescription className="text-orange-800">
          <strong>Atenção:</strong> Você precisa fazer todas as escolhas antes de confirmar.
        </AlertDescription>
      </Alert>

      {/* Lista de ambiguidades */}
      {ambiguousItems.map((item, index) => {
        const currentResolution = resolutions.find(
          (res) => res.store_id === item.store_id
        );

        return (
          <div key={item.store_id} className="space-y-4">
            {/* Informações da loja */}
            <div className="space-y-1">
              <h4 className="font-semibold text-base">
                Loja {item.store_code} - {item.store_name}
              </h4>
              <p className="text-sm text-muted-foreground">
                Excel: "{item.regional_name_from_excel}"
              </p>
            </div>

            {/* Radio group */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Escolha o regional correto:</Label>
              <RadioGroup
                value={currentResolution?.selected_regional_id?.toString() || ""}
                onValueChange={(value) => {
                  handleRegionalSelect(item.store_id, value);
                }}
                className="space-y-2"
              >
                {item.matches.map((match: RegionalMatch) => (
                  <div
                    key={match.id}
                    className="flex items-center space-x-2 rounded-md border p-3 hover:bg-accent cursor-pointer"
                  >
                    <RadioGroupItem
                      value={match.id.toString()}
                      id={`regional-${item.store_id}-${match.id}`}
                    />
                    <Label
                      htmlFor={`regional-${item.store_id}-${match.id}`}
                      className="flex-1 cursor-pointer"
                    >
                      {match.name}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {/* Divider entre itens */}
            {index < ambiguousItems.length - 1 && <Separator />}
          </div>
        );
      })}

      {/* Alert informativo */}
      {!allResolved && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Você precisa fazer todas as escolhas antes de confirmar (
            {resolutions.filter((r) => r.selected_regional_id !== null).length}/
            {resolutions.length})
          </AlertDescription>
        </Alert>
      )}

      {/* Footer com botões */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button 
          onClick={handleConfirm} 
          disabled={!allResolved}
        >
          Confirmar Escolhas {allResolved ? "Ok" : `(${resolutions.filter((r) => r.selected_regional_id !== null).length}/${resolutions.length})`}
        </Button>
      </div>
    </div>
  );
};